import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../utils/i18n';
import { dashboardAPI } from '../utils/api';
import { imageUrl } from '../utils/format';
import ServiceCard from '../components/ServiceCard';
import './HomePage.css';

const HomePage = () => {
  const navigate = useNavigate();
  const { user, language, changeLanguage } = useAuth();
  const { t } = useTranslation(language);
  const [stats, setStats] = useState({
    commission: 0,
    newCustomers: 0,
    totalLoans: 0,
    approvedLoans: 0,
    disbursedLoans: 0,
    pendingLoans: 0,
    rejectedLoans: 0,
    availableBalance: 0,
  });
  const [banks, setBanks] = useState([]);
  const [showAllBanks, setShowAllBanks] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [avatarError, setAvatarError] = useState(false);

  useEffect(() => {
    fetchDashboard();
    fetchBanks();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await dashboardAPI.getStats();
      const apiStats = response.data.stats || response.data;
      setStats({
        commission: apiStats.currentMonthCommission || 0,
        newCustomers: apiStats.newCustomers || 0,
        totalLoans: apiStats.totalLoans || 0,
        approvedLoans: apiStats.approvedLoans || 0,
        disbursedLoans: apiStats.disbursedLoans || 0,
        pendingLoans: apiStats.pendingLoans || 0,
        rejectedLoans: apiStats.rejectedLoans || 0,
        availableBalance: apiStats.availableBalance || 0,
      });
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await dashboardAPI.getBanks();
      setBanks(response.data.banks || []);
    } catch (error) {
      console.error('Failed to fetch banks:', error);
    }
  };

  const handleBankClick = (bank) => {
    if (bank.branches && bank.branches.length === 1) {
      // Only one branch, open link directly
      window.open(bank.branches[0].link, '_blank');
    } else if (bank.branches && bank.branches.length > 1) {
      // Multiple branches, show selection
      setSelectedBank(bank);
    }
  };

  const handleBranchClick = (branch) => {
    window.open(branch.link, '_blank');
    setSelectedBank(null);
  };

  const services = [
    {
      id: 'loan',
      icon: '🏦',
      title: t('loanService'),
      status: t('active'),
      isActive: true,
      path: '/loan/list',
    },
    {
      id: 'study-abroad',
      icon: '🎓',
      title: t('studyAbroadService'),
      status: t('comingSoon'),
      isActive: true,
      path: '/coming-soon',
    },
    {
      id: 'creditcard',
      icon: '💳',
      title: t('creditCardService'),
      status: t('comingSoon'),
      isActive: true,
      path: '/coming-soon',
    },
    {
      id: 'visa',
      icon: '🛂',
      title: t('visaService'),
      status: t('comingSoon'),
      isActive: true,
      path: '/coming-soon',
    },
    {
      id: 'sim',
      icon: '📱',
      title: t('simService'),
      status: t('comingSoon'),
      isActive: true,
      path: '/coming-soon',
    },
    {
      id: 'phone',
      icon: '📞',
      title: t('phoneService'),
      status: t('comingSoon'),
      isActive: true,
      path: '/coming-soon',
    },
    {
      id: 'flight',
      icon: '✈️',
      title: t('flightService'),
      status: t('comingSoon'),
      isActive: true,
      path: '/coming-soon',
    },
  ];

  const languages = [
    { code: 'vi', label: '🇻🇳 Tiếng Việt', flag: '🇻🇳' },
    { code: 'en', label: '🇬🇧 English', flag: '🇬🇧' },
    { code: 'ko', label: '🇰🇷 한국어', flag: '🇰🇷' },
  ];

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="home-page">
      {/* Header with gradient and decorative shapes */}
      <header className="home-header">
        <div className="header-top">
          <div className="header-brand">
            <img src="/logo.jpg" alt="Logo" className="brand-logo" />
            <span className="brand-name">KMO</span>
          </div>
          <div className="language-selector">
            <select
              value={language}
              onChange={(e) => changeLanguage(e.target.value)}
              className="language-select"
            >
              {languages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div
          className="greeting-section"
          onClick={() => navigate('/profile')}
          style={{ cursor: 'pointer', position: 'relative' }}
        >
          <div style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            background: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '2px solid rgba(255, 255, 255, 0.8)',
          }}>
            {user?.url ? (
              <img
                src={(user.url)}
                alt="Avatar"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
                // onError={() => setAvatarError(true)}
              />
            ) : (
              <span style={{ fontSize: '24px', color: '#667eea' }}>👤</span>
            )}
          </div>
          <p className="greeting-subtitle">{t('hello')}</p>
          <h1 className="greeting-title">{user?.name || user?.username}</h1>
          <p className="greeting-email">{user?.email}</p>

          {/* Available Balance */}
          <div className="balance-display">
            <p className="balance-label">
              {language === 'vi' ? 'Tổng số dư' : language === 'ko' ? '총 잔액' : 'Total Balance'}
            </p>
            <p className="balance-amount" style={{ color: 'white' }}>
              {stats.availableBalance?.toLocaleString(language === 'vi' ? 'vi-VN' : language === 'ko' ? 'ko-KR' : 'en-US')} {language === 'ko' ? '₩' : '₫'}
            </p>
          </div>
        </div>

        <div className="header-decoration">
          <div className="deco-shape deco-1"></div>
          <div className="deco-shape deco-2"></div>
          <div className="deco-shape deco-3"></div>
        </div>
      </header>

      <main className="home-main">
        {/* Quick Actions Grid */}
        <section className="quick-actions">
          <div className="actions-grid">
            {services.map((service) => (
              <ServiceCard key={service.id} {...service} />
            ))}
          </div>
        </section>

        {/* Loan Statistics Grid */}
        <section className="stats-section">
          <div className="stats-grid">
            <div className="stat-box">
              <p className="stat-value">{stats.totalLoans || 0}</p>
              <p className="stat-label">{t('draft')}</p>
            </div>

            <div className="stat-box">
              <p className="stat-value">{stats.pendingLoans || 0}</p>
              <p className="stat-label">{language === 'vi' ? 'Chờ duyệt' : language === 'ko' ? '대기 중' : 'Pending'}</p>
            </div>

            <div className="stat-box">
              <p className="stat-value">{stats.disbursedLoans || 0}</p>
              <p className="stat-label">{language === 'vi' ? 'Đã giải ngân' : language === 'ko' ? '지급됨' : 'Disbursed'}</p>
            </div>
          </div>
        </section>

        {/* Quick Bank Links */}
        {banks.length > 0 && (
          <section className="banks-section">
            <div className="section-header">
              <h2 className="section-title">
                {language === 'vi' ? 'Đăng ký vay nhanh' : language === 'ko' ? '빠른 대출 신청' : 'Quick Loan Apply'}
              </h2>
              {banks.length > 3 && (
                <button className="see-all-btn" onClick={() => setShowAllBanks(true)}>
                  {language === 'vi' ? 'Xem tất cả' : language === 'ko' ? '모두 보기' : 'See All'}
                </button>
              )}
            </div>
            <div className="banks-grid">
              {banks.slice(0, 3).map((bank) => (
                <button
                  key={bank.id}
                  className="bank-btn"
                  onClick={() => handleBankClick(bank)}
                >
                  <span className="bank-icon">🏦</span>
                  <span className="bank-name">{bank.name}</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* All Banks Modal */}
        {showAllBanks && (
          <div className="modal-overlay" onClick={() => setShowAllBanks(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{language === 'vi' ? 'Chọn ngân hàng' : language === 'ko' ? '은행 선택' : 'Select Bank'}</h3>
                <button className="modal-close" onClick={() => setShowAllBanks(false)}>×</button>
              </div>
              <div className="modal-body">
                {banks.map((bank) => (
                  <button
                    key={bank.id}
                    className="bank-list-item"
                    onClick={() => {
                      setShowAllBanks(false);
                      handleBankClick(bank);
                    }}
                  >
                    <span className="bank-icon">🏦</span>
                    <span className="bank-name">{bank.name}</span>
                    <span className="branch-count">{bank.branches?.length || 0} {language === 'vi' ? 'chi nhánh' : language === 'ko' ? '지점' : 'branches'}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Branch Selection Modal */}
        {selectedBank && (
          <div className="modal-overlay" onClick={() => setSelectedBank(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{selectedBank.name} - {language === 'vi' ? 'Chọn chi nhánh' : language === 'ko' ? '지점 선택' : 'Select Branch'}</h3>
                <button className="modal-close" onClick={() => setSelectedBank(null)}>×</button>
              </div>
              <div className="modal-body">
                {selectedBank.branches?.map((branch) => (
                  <button
                    key={branch.id}
                    className="branch-list-item"
                    onClick={() => handleBranchClick(branch)}
                  >
                    <span className="branch-icon">📍</span>
                    <div className="branch-info">
                      <span className="branch-name">{branch.name}</span>
                      {branch.address && <span className="branch-address">{branch.address}</span>}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Company Information Footer */}
        <section className="company-info-section">
          <div className="company-info-card">
            <div className="company-info-content">
              <p className="company-name">(주)KMO</p>
              <p className="company-phone">010-8351-1179</p>
              <p className="company-address">경기도 수원시 팔달구 덕영대로 933, 2층</p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default HomePage;
